/**
 * Extract Emails Tool
 *
 * Extract all email addresses from text using regex.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

// RFC 5322 inspired pattern — covers most practical email addresses
const EMAIL_REGEX = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*/g;

export const ExtractEmailsTool: ITransformerTool = {
  id: 'extract-emails',
  name: 'Extract Emails',
  description: 'Extract all email addresses from text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['email', 'extract', 'find', 'parse', 'address', 'regex'],

  seo: {
    slug: 'email-extractor',
    pageTitle: 'Email Extractor - Free Online Tool | Prism',
    metaDescription: 'Extract all email addresses from any text. Outputs a clean, deduplicated list. Fast, private, runs entirely in your browser.',
    keywords: ['email extractor', 'extract emails online', 'find emails in text', 'email parser', 'email finder'],
    relatedTools: ['extract-urls', 'find-replace', 'regex-tester'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'deduplicate',
      type: 'boolean',
      label: 'Remove Duplicates',
      defaultValue: true,
      description: 'Remove duplicate email addresses from the output',
    },
    {
      name: 'lowercase',
      type: 'boolean',
      label: 'Lowercase',
      defaultValue: false,
      description: 'Convert all emails to lowercase',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '');
    if (!text.trim()) return '';

    const deduplicate = config.deduplicate !== false;
    const lowercase = config.lowercase === true;

    let emails = Array.from(text.matchAll(EMAIL_REGEX)).map((m) => m[0]);

    if (lowercase) {
      emails = emails.map((e) => e.toLowerCase());
    }

    if (deduplicate) {
      emails = [...new Set(emails)];
    }

    if (emails.length === 0) {
      return '(no email addresses found)';
    }

    return emails.join('\n');
  },
};
